import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import { BORDER_RADIUS_SMALL } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import {
  DaysOfWeek,
  FulfillmentName,
  FulfillmentType,
  RestaurantVp_SubscriptionFragment,
} from '@/types';
import { displayCurrencyPlaceholder, formatCurrency } from '@/utils/money';
import { formatDateByWeekday } from '@/utils/date';
import { ItemListOption } from '@/contexts/item-context-types';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { MultiStyleText } from '@/components/multi-style-text';
import { SubscriptionListItemContent } from './subscription-list-item-content';
import { Text, View } from 'react-native';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';

interface SubscriptionConfig {
  frequency_days: number;
  quantity: number;
  start_date: string;
  weekday: DaysOfWeek;
}

interface Props {
  nextDeliveryDate: string;
  onSubscriptionOptionsSelect: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  subscription: RestaurantVp_SubscriptionFragment;
  variantPack: ItemListOption;
}

const MENU_ICON_HITSLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export const SubscriptionListItem: React.FC<Props> = ({
  nextDeliveryDate,
  onSubscriptionOptionsSelect,
  subscription,
  variantPack,
}) => {
  const { config, fulfillment_settings_uuid, uuid, pause_until } = subscription;
  const { metadata } = variantPack;

  const subscriptionConfig = config as SubscriptionConfig;

  const { availableOrderFulfillments } = useNewOrderContext();
  const { isLargeLayout } = useLayoutContext();
  const [styles, theme] = useStyles();
  const selectedFulfillment = useMemo(
    () =>
      availableOrderFulfillments.find(
        (item) => item.fulfillment_settings_id === fulfillment_settings_uuid,
      ),
    [availableOrderFulfillments, fulfillment_settings_uuid],
  );

  const displayName =
    selectedFulfillment?.fulfillment_type ===
    FulfillmentType.FulfillmentShippingOption
      ? 'order'
      : null;

  const nextFulfillmentDate = useMemo(
    () => formatDateByWeekday(moment(nextDeliveryDate)),
    [nextDeliveryDate],
  );

  const weeklyFrequency = subscriptionConfig.frequency_days / 7;
  const textSizeStyle = isLargeLayout ? styles.textMedium : styles.textSmall;
  const isPaused = useMemo(() => {
    if (pause_until) {
      const pauseUntil = moment(pause_until);
      return moment().isSameOrBefore(pauseUntil);
    }
    return false;
  }, [pause_until]);

  return (
    <>
      <View style={styles.flex}>
        <View style={[styles.row, styles.vpRow]}>
          <View style={styles.flex}>
            {metadata ? (
              <SubscriptionListItemContent
                listDisplaySize={
                  isLargeLayout
                    ? ListDisplaySize.Standard
                    : ListDisplaySize.Medium
                }
                option={variantPack}
                rightComponent={
                  <Button
                    containerStyle={styles.menuButton}
                    hitSlop={MENU_ICON_HITSLOP}
                    Icon={MenuHorizontalIcon}
                    iconProps={{ fill: theme.sk.color.icon.tertiary }}
                    onPress={() => onSubscriptionOptionsSelect(uuid)}
                    size="small"
                    testID={`subscription-item-menu-button-${uuid}`}
                    trackingData={{
                      name: 'subscription_item.menu_button',
                      properties: {
                        subscriptionUUID: uuid,
                      },
                    }}
                    type="tertiary"
                  />
                }
              />
            ) : null}
          </View>
        </View>

        <View style={styles.row}>
          <Text style={[styles.text, textSizeStyle]}>
            {weeklyFrequency === 1
              ? i18n.t('weekly_frequency_singular')
              : i18n.t('weekly_frequency_plural', { count: weeklyFrequency })}
          </Text>
          <Text style={[styles.text, textSizeStyle]}>
            {i18n.t(
              config.quantity === 1 ? 'unit_count' : 'unit_count_plural',
              {
                count: config.quantity,
              },
            )}
          </Text>
        </View>
        <View style={styles.row}>
          <View style={styles.nextFulfillmentInfoRow}>
            {isPaused ? (
              <View
                style={styles.pauseIcon}
                testID={`icon-paused-subscription-${uuid}`}
              />
            ) : null}
            <MultiStyleText
              defaultStyle={[styles.text, textSizeStyle]}
              emphasisStyle={[styles.textHighlighted, textSizeStyle]}
            >
              {translateFulfillmentType(
                selectedFulfillment?.fulfillment_name ??
                  FulfillmentName.Delivery,
                isPaused
                  ? 'subscription_resumes.fulfillment_type'
                  : 'subscription.fulfillment_type',
                displayName,
                {
                  date: nextFulfillmentDate,
                },
              )}
            </MultiStyleText>
          </View>
          <Text style={[styles.textHighlighted, textSizeStyle]}>
            {metadata?.unitPriceInMicros
              ? formatCurrency(
                  metadata.unitPriceInMicros * config.quantity,
                  metadata.currencyCode,
                )
              : displayCurrencyPlaceholder(metadata?.currencyCode)}
          </Text>
        </View>
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  flex: {
    flex: 1,
  },
  menuButton: {
    marginRight: -12,
  },
  nextFulfillmentInfoRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  pauseIcon: {
    backgroundColor: theme.sk.color.icon.warning.secondary,
    borderRadius: BORDER_RADIUS_SMALL,
    height: 8,
    width: 8,
  },
  row: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  text: {
    color: theme.sk.color.text.tertiary,
    fontFamily: theme.fonts.primary,
  },
  textHighlighted: {
    color: theme.sk.color.text.primary,
    fontFamily: theme.fonts.primarySemiBold,
  },
  textMedium: {
    fontSize: theme.fontSizes.medium,
    lineHeight: 24,
  },
  textSmall: {
    fontSize: theme.fontSizes.small,
    lineHeight: 21,
  },
  vpRow: {
    marginBottom: 16,
  },
}));
